import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiArrowRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { getLocalized } from '../../utils/i18nHelpers';
import { getImageUrl } from '../../utils/imageHelpers';
import { motion, AnimatePresence } from 'framer-motion';

const HomeSlider = ({ settings }) => {
    const { i18n, t } = useTranslation();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const defaultSlides = [
        {
            id: 1,
            image: "",
            subtitle: "SAF GÜMÜŞ KOLEKSİYONU",
            title: "Gümüşün Zarif Işıltısı",
            description: "925 ayar gümüşten üretilen özel tasarımlarımızla şıklığı ve zarafeti günlük yaşamınıza taşıyın.",
            link: "/products?category=gumus",
            buttonText: "ÜRÜNLERİMİZİ KEŞFEDİN"
        },
        {
            id: 2,
            image: "",
            subtitle: "ALTIN KOLEKSİYONU",
            title: "Zamansız\nZarafet",
            description: "Altının sıcaklığıyla teninizde hayat bulan tasarımlar.",
            link: "/products?category=altin",
            buttonText: "ALTIN KOLEKSİYONU"
        },
        {
            id: 3,
            image: "",
            subtitle: "GÜMÜŞ",
            title: "Modern\nDokunuş",
            description: "Gümüşün modern ve cesur duruşuyla stilinizi tamamlayın.",
            link: "/products?category=gumus",
            buttonText: "GÜMÜŞ KOLEKSİYONU"
        }
    ];

    const slides = settings?.heroSlides?.length > 0 ? settings.heroSlides : defaultSlides;

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
        }, 6000);
        return () => clearInterval(timer);
    }, [slides.length]);

    const nextSlide = () => setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    const prevSlide = () => setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));

    return (
        <div className="relative h-[550px] md:h-[85vh] lg:h-screen w-full overflow-hidden bg-black text-white">
            {/* Slides Container */}
            <div
                className="absolute inset-0 flex transition-transform duration-1000 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
                {slides.map((slide, index) => {
                    const titleFont = slide.titleFont || 'serif';
                    const titleSize = slide.titleSize || 'lg';
                    const titleColor = slide.titleColor || 'white';
                    
                    const subtitleFont = slide.subtitleFont || 'sans';
                    const subtitleColor = slide.subtitleColor || 'primary';
                    
                    const descColor = slide.descColor || 'gray';

                    const titleFontClass = titleFont === 'sans' ? 'font-sans' : 'font-serif';
                    const titleColorClass = titleColor === 'primary' ? 'text-primary' : 'text-white';
                    
                    let titleSizeClass = 'text-4xl md:text-6xl lg:text-8xl';
                    if (titleSize === 'sm') {
                        titleSizeClass = 'text-2xl md:text-4xl lg:text-5xl';
                    } else if (titleSize === 'md') {
                        titleSizeClass = 'text-3xl md:text-5xl lg:text-6xl';
                    }

                    const subtitleFontClass = subtitleFont === 'serif' 
                        ? 'font-serif italic' 
                        : 'font-sans font-semibold tracking-[0.2em] uppercase';
                    
                    const subtitleColorClass = subtitleColor === 'white' 
                        ? 'text-white' 
                        : subtitleColor === 'gray' 
                            ? 'text-gray-400' 
                            : 'text-primary';

                    const descColorClass = descColor === 'white' ? 'text-white' : 'text-gray-300';

                    return (
                        <div key={slide.id || slide._id || index} className="relative w-full h-full flex-shrink-0">
                            {/* Background Media */}
                            {slide.image && /\.(mp4|webm|ogg|mov)$/i.test(slide.image) ? (
                                <div className="absolute inset-0 overflow-hidden">
                                    <video
                                        src={getImageUrl(slide.image) || slide.image}
                                        className="w-full h-full object-cover scale-105"
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload={index === 0 ? "auto" : "metadata"}
                                        aria-label="Tanıtım videosu"
                                    >
                                        <track kind="captions" src="" label="Sessiz video" />
                                    </video>
                                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
                                </div>
                            ) : (
                                <div className="absolute inset-0 overflow-hidden bg-gradient-to-br from-gray-950 via-neutral-900 to-black">
                                    {slide.image && (
                                        <img
                                            src={getImageUrl(slide.image) || slide.image}
                                            alt={getLocalized(slide, 'title', i18n.language) || slide.title || "Slider görseli"}
                                            fetchPriority={index === 0 ? "high" : "low"}
                                            loading={index === 0 ? "eager" : "lazy"}
                                            decoding="async"
                                            className="w-full h-full object-cover transition-transform duration-[10000ms] scale-105"
                                        />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
                                </div>
                            )}

                            {/* Content - Left Aligned */}
                            <div className="absolute inset-0 flex items-center justify-start px-8 md:px-24 lg:px-32">
                                <AnimatePresence mode="wait">
                                    {currentSlide === index && (
                                        <motion.div
                                            key={slide.id || slide._id || index}
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -20 }}
                                            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay: 0.2 }}
                                            className="max-w-2xl mt-20"
                                        >
                                            {slide.subtitle && (
                                                <motion.span
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.6, delay: 0.3 }}
                                                    className={`block text-xs md:text-sm mb-4 ${subtitleFontClass} ${subtitleColorClass}`}
                                                >
                                                    {getLocalized(slide, 'subtitle', i18n.language) || slide.subtitle}
                                                </motion.span>
                                            )}

                                            <motion.h2
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.6, delay: 0.4 }}
                                                className={`font-medium mb-6 leading-tight whitespace-pre-line ${titleFontClass} ${titleSizeClass} ${titleColorClass}`}
                                            >
                                                {getLocalized(slide, 'title', i18n.language) || slide.title}
                                            </motion.h2>

                                            {slide.description && (
                                                <motion.p
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.6, delay: 0.5 }}
                                                    className={`text-base md:text-lg font-light mb-10 max-w-lg ${descColorClass}`}
                                                >
                                                    {getLocalized(slide, 'description', i18n.language) || slide.description}
                                                </motion.p>
                                            )}

                                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.6, delay: 0.6 }}
                                            >
                                                <Link
                                                    to={slide.link || '/products'}
                                                    className="inline-flex items-center gap-4 border border-white/50 text-white px-8 py-4 text-xs font-bold tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-all duration-300"
                                                >
                                                    {getLocalized(slide, 'buttonText', i18n.language) || slide.buttonText}
                                                    <FiArrowRight />
                                                </Link>
                                            </motion.div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Left/Right Arrows */}
            <div className="absolute top-1/2 -translate-y-1/2 left-4 md:left-8 z-20 hidden md:block">
                <button
                    onClick={prevSlide}
                    className="w-12 h-12 flex items-center justify-center border border-white/30 text-white hover:bg-white/10 hover:border-white transition-all backdrop-blur-sm"
                    aria-label="Önceki slayt"
                >
                    <FiChevronLeft className="text-xl" />
                </button>
            </div>
            <div className="absolute top-1/2 -translate-y-1/2 right-4 md:right-8 z-20 hidden md:block">
                <button
                    onClick={nextSlide}
                    className="w-12 h-12 flex items-center justify-center border border-white/30 text-white hover:bg-white/10 hover:border-white transition-all backdrop-blur-sm"
                    aria-label="Sonraki slayt"
                >
                    <FiChevronRight className="text-xl" />
                </button>
            </div>

            {/* Vertical SCROLL text */}
            <div className="absolute bottom-12 left-8 md:left-16 z-20 hidden md:flex flex-col items-center gap-4">
                <span className="text-white/60 text-[10px] tracking-[0.3em] uppercase rotate-180" style={{ writingMode: 'vertical-rl' }}>
                    SCROLL
                </span>
                <div className="w-[1px] h-12 bg-white/30"></div>
            </div>

            {/* Pagination Counter (e.g. 01 / 03) */}
            <div className="absolute bottom-12 right-8 md:right-16 z-20 font-serif text-sm tracking-widest text-white/80">
                0{currentSlide + 1} / 0{slides.length}
            </div>
            
            {/* Minimal line indicator */}
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-4 z-20">
                {slides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`h-[1px] transition-all duration-300 ${currentSlide === index ? 'w-12 bg-white' : 'w-8 bg-white/30 hover:bg-white/60'}`}
                        aria-label={`Slayt ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default HomeSlider;
